import React, { useEffect, useState } from "react";
import _ from "lodash";
import { QueryRequestHelper } from "@_common/query-request-helper";
import { getDashboardSelfComplianceUserEntityData, getDashboardSelfComplianceUserEntityDataByLevel } from "@services/dashboard.service";
import { convertDate } from "@_common/date-helpers";
import "./table.scss";
import PaginationPage from "@components/pagination/pagination";

type DataKeys =
  | "assigned"
  | "complied"
  | "delayed"
  | "delay_reported"
  | "non_complied"
  | "posing"
  | "re_opened"
  | "event_not_occured"
  | "awaiting_approval"
  | "reported_non_complied";

interface GroupChartItem {
  group_name: string;
  group_display_name: string;
  group_id: string;
  data: Record<DataKeys, number>;
}

interface ComplianceData {
  group_name: string;
  group_display_name: string;
  group_id: string;
  data: Record<DataKeys, number>;
}

interface TableProps {
  parentData: GroupChartItem[];
  handleNavigate: (groupId: string, status: string) => void;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  level?: number;
}

interface PaginationState {
  totalCount: number;
  pageNo: number;
  limit: number;
}

const ExpandableComplianceTableMyActivity: React.FC<TableProps> = ({
  parentData = [],
  handleNavigate,
  fromDate,
  toDate,
  limit = 10,
  level = 1,
}) => {
  const [groupedData, setGroupedData] = useState<{ [key: string]: ComplianceData[] }>({});
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [childData, setChildData] = useState<{ [key: string]: ComplianceData[] }>({});
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [paginationStates, setPaginationStates] = useState<{ [key: string]: PaginationState }>({});

  const tableHeaders: DataKeys[] = [
    "assigned",
    "complied",
    "delayed",
    "delay_reported",
    "non_complied",
    "posing",
    "re_opened",
    "event_not_occured",
    "awaiting_approval",
    "reported_non_complied"
  ];

  useEffect(() => {
    const grouped: { [key: string]: ComplianceData[] } = {};
    
    if (Array.isArray(parentData)) {
      parentData.forEach((item) => {
        if (!grouped[item.group_name]) {
          grouped[item.group_name] = [];
        }
        
        grouped[item.group_name].push({
          ...item,
          group_display_name: `${item.group_name} Overview`
        });
      });
    }

    setGroupedData(grouped);
  }, [parentData]);

  const fetchChildData = async (groupName: string, pageNo: number = 1, pageSize: number = limit) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    try {
      setLoading((prev) => ({ ...prev, [groupName]: true }));

      let payload: any = {
        page_no: pageNo,
        page_size: pageSize,
        from_date: convertDate(fromDate),
        to_date: convertDate(toDate),
        level: level,
        entity_name: groupName,
        dashboard_type: params.dashboard_type,
      };

      Object.keys(payload).forEach((key) => {
        if (!payload[key] || payload[key] === undefined) {
          delete payload[key];
        }
      });

      let queryRequest = QueryRequestHelper({ ...payload });
      let resp = await getDashboardSelfComplianceUserEntityDataByLevel(
        queryRequest,
        true
      );

      if (resp?.data?.by_entity) {
        console.log(resp, "---resp--")
        const childDataArray = Object.entries(resp.data.by_entity).map(([key, value]: [string, any]) => {
          const [id, name, , location] = key.split('::');
          return {
            group_name: name,
            group_display_name: location 
              ? `${name} <span class="p-1 graph_small_text ms-2">${location}</span>` 
              : name,
            group_id: id,
            data: value as Record<DataKeys, number>
          };
        
        });

        setChildData(prev => ({
          ...prev,
          [groupName]: childDataArray
        }));

        setPaginationStates(prev => ({
          ...prev,
          [groupName]: {
            totalCount: resp.data.pagination.total || 0,
            pageNo: pageNo,
            limit: pageSize
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching child data:', error);
    } finally {
      setLoading((prev) => ({ ...prev, [groupName]: false }));
    }
  };

  const toggleGroup = async (groupName: string) => {
    if (expandedGroups.includes(groupName)) {
      setExpandedGroups(prev => prev.filter(g => g !== groupName));
    } else {
      setExpandedGroups(prev => [...prev, groupName]);
      if (!childData[groupName]) {
        await fetchChildData(groupName);
      }
    }
  };

  const handleChangePagination = (groupName: string, pageNo: number) => {
    const currentState = paginationStates[groupName];
    if (currentState) {
      fetchChildData(groupName, pageNo, currentState.limit);
    }
  };

  const handleChangeLimit = (groupName: string, newLimit: number) => {
    fetchChildData(groupName, 1, newLimit);
  };

  const formatHeader = (header: string): string => {
    if (header === "event_not_occured") return "Event Not Occurred";
    return header.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  const renderTableCell = (item: ComplianceData, header: DataKeys, isOverview: boolean = false) => {
    const value = item.data[header] || 0;
    
    if (isOverview || value === 0) {
      return <td className="text-right">{value}</td>;
    }

    return (
      <td className="text-right">
        <a
          className="text-blue-600 text-decoration-underline cursor-pointer"
          onClick={() => handleNavigate(item.group_id, header.toUpperCase())}
        >
          {value}
        </a>
      </td>
    );
  };



  const renderTableRow = (item: ComplianceData, isOverview: boolean = false) => (
    <tr key={item.group_id} className={`border-b ${isOverview ? "bg-gray-50 border-bottom-10" : "bg-white "}`}>
      <td className="">
        <div className="ms-2">
          {/* <span>{item.group_display_name}</span> */}
          <span dangerouslySetInnerHTML={{ __html: item.group_display_name }} />
          {/* {!isOverview && (
            <span className="text-sm text-gray-500">{item.group_id}</span>
          )} */}
        </div>
      </td>
      {tableHeaders.map(header => renderTableCell(item, header, isOverview))}
      <td className="text-center">
        {isOverview ? (
          <span
            onClick={() => toggleGroup(item.group_name)}
            className="cursor-pointer"
          >
            {loading[item.group_name] ? (
              <span className="inline-block animate-spin reload-icon">↻</span>
            ) : (
              expandedGroups.includes(item.group_name) ? <img src={`/images/dashboard/tables/remove-circle.svg`}/> : 
              <img src={`/images/dashboard/tables/add-circle.svg`}/>
            )}
          </span>
        ) : (
          <span></span>
        )}
      </td>
    </tr>
  );

  return (
    <div className="overflow-x-auto shadow-md rounded-lg self-compliance-container self-compliance-container-new">
      <table className="min-w-full bg-white">
        <thead>
          <tr>
            <th className="text-left font-semibold">Group Name</th>
            {tableHeaders.map(header => (
              <th key={header} className="">
                <h5 className="header">{formatHeader(header)}</h5>
              </th>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(groupedData).map(([groupName, items]) => (
            <React.Fragment key={groupName}>
              {items[0] && renderTableRow(items[0], true)}
              {expandedGroups.includes(groupName) && (
                <tr className="bg-white border-bottom">
                  <td colSpan={tableHeaders.length + 2} className="p-0">
                    <div className="mb-4">
                      <table className="mt-0">
                        <tbody>
                          {childData[groupName]?.map(item => renderTableRow(item))}
                        </tbody>
                      </table>
                      {paginationStates[groupName] && (
                        <div className="mt-4 mx-2">
                          {paginationStates[groupName].totalCount > paginationStates[groupName].limit ? (
                            <PaginationPage
                              totalCount={paginationStates[groupName].totalCount}
                              limit={paginationStates[groupName].limit}
                              pageNumber={paginationStates[groupName].pageNo}
                              handleChangePagination={(page:any) => handleChangePagination(groupName, page)}
                              handleChangeLimit={(limit:any) => handleChangeLimit(groupName, limit)}
                            />
                          ) : (
                            <div className="footer-wrapper d-flex">
                              Total Count:&nbsp;&nbsp;{" "}
                              <p style={{ color: "rgb(13, 110, 253)" }}>{paginationStates[groupName].totalCount}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ExpandableComplianceTableMyActivity;